import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { UserPlus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { RoleBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function initials(n = "") {
  return n.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}

const ROLE_OPTS = ["Aksjonær", "Styremedlem", "Styreleder", "Nestleder", "Varamedlem", "Daglig leder", "Observatør"];

export default function Access() {
  const { companyId } = useParams();
  const [members, setMembers] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", national_id: "", email: "", role: "Aksjonær", is_owner: false });

  const load = useCallback(() => api.get(`/companies/${companyId}/members`).then((r) => setMembers(r.data)), [companyId]);

  useEffect(() => {
    setMembers(null);
    load();
  }, [load]);

  const invite = async () => {
    if (!form.name || !form.national_id) {
      toast.error("Fyll inn navn og fødselsnummer");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/companies/${companyId}/invite`, form);
      toast.success(`${form.name} er gitt tilgang`);
      setOpen(false);
      setForm({ name: "", national_id: "", email: "", role: "Aksjonær", is_owner: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/companies/${companyId}/members/${m.membership_id}`);
      toast.success("Tilgang fjernet");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Tilgang" description="Gi medeiere og andre tilgang til styrerommet. Tildelte brukere kommer i tillegg til rollene fra Brønnøysundregisteret.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 active:scale-[0.98]" data-testid="invite-btn"><UserPlus className="h-4 w-4" /> Gi tilgang</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Gi tilgang</DialogTitle>
              <DialogDescription>Personen logger inn med BankID og får tilgang til dette selskapet.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Navn</Label><Input data-testid="invite-name-input" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Fødselsnummer</Label><Input data-testid="invite-nid-input" className="mt-1.5" value={form.national_id} onChange={(e) => setForm({ ...form, national_id: e.target.value })} placeholder="11 siffer" /></div>
                <div><Label>E-post</Label><Input className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </div>
              <div>
                <Label>Rolle</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLE_OPTS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.is_owner} onCheckedChange={(v) => setForm({ ...form, is_owner: !!v })} data-testid="invite-owner-checkbox" />
                Marker som medeier
              </label>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Avbryt</Button>
              <Button onClick={invite} disabled={saving} data-testid="save-invite-btn">{saving ? "Lagrer …" : "Gi tilgang"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="space-y-3">
        {members === null
          ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)
          : members.map((m, i) => (
              <motion.div key={m.membership_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} data-testid={`member-row-${m.membership_id}`} className="flex items-center gap-4 rounded-xl border border-border/60 bg-card p-4 shadow-sm">
                <Avatar className="h-10 w-10"><AvatarImage src={m.avatar || undefined} /><AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">{initials(m.name)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium">{m.name}</p>
                    {m.is_owner ? <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400">Eier</span> : null}
                  </div>
                  <p className="text-xs text-muted-foreground">{m.email || "—"}{m.granted_by ? ` · tildelt av ${m.granted_by}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col items-end gap-1.5">
                    <RoleBadge role={m.role} />
                  </div>
                  {m.source === "Tildelt" ? (
                    <Button variant="ghost" size="icon" onClick={() => remove(m)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-member-${m.membership_id}`}><Trash2 className="h-4 w-4" /></Button>
                  ) : null}
                </div>
              </motion.div>
            ))}
      </div>

      <p className="mt-6 flex items-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" /> Roller merket «Proff» / «Brønnøysundregisteret» kan ikke fjernes manuelt.
      </p>
    </div>
  );
}
